import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { mdxComponents, proseClassNames } from "./MdxProse";

type Block =
  | { type: "heading"; level: 1 | 2 | 3; text: string }
  | { type: "paragraph"; text: string }
  | { type: "blockquote"; lines: string[] }
  | { type: "ul"; items: string[] }
  | { type: "ol"; items: string[] }
  | { type: "pre"; code: string }
  | { type: "image"; src: string; alt: string; width?: number; height?: number };

function parseBlocks(markdown: string): Block[] {
  const lines = markdown.replace(/\r\n/g, "\n").split("\n");
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i].trim();
    if (!line) {
      i += 1;
      continue;
    }

    if (line.startsWith("```")) {
      const code: string[] = [];
      i += 1;
      while (i < lines.length && !lines[i].trim().startsWith("```")) {
        code.push(lines[i]);
        i += 1;
      }
      blocks.push({ type: "pre", code: code.join("\n") });
      i += 1;
      continue;
    }

    const image = /^!\[([^\]]*)\]\(([^)]+)\)$/.exec(line);
    if (image?.[2]?.trim()) {
      blocks.push({ type: "image", alt: image[1].trim(), src: image[2].trim() });
      i += 1;
      continue;
    }

    // go-cms embeds inline images as a self-closing <CMSImage> JSX-style tag
    // (src/alt are quoted, width/height use JSX brace literals). The legacy
    // MDX path rendered JSX natively; this CMS markdown path must parse it.
    const cmsImage = line.match(/^<CMSImage\b([^>]*?)\/?>$/i);
    if (cmsImage) {
      const attrs = cmsImage[1];
      const src = attrs.match(/\bsrc\s*=\s*["']([^"']+)["']/i)?.[1]?.trim();
      if (src) {
        const alt = attrs.match(/\balt\s*=\s*["']([^"']*)["']/i)?.[1]?.trim() ?? "";
        const width = attrs.match(/\bwidth\s*=\s*\{?\s*["']?(\d+)["']?\s*\}?/i)?.[1];
        const height = attrs.match(/\bheight\s*=\s*\{?\s*["']?(\d+)["']?\s*\}?/i)?.[1];
        blocks.push({
          type: "image",
          src,
          alt,
          width: width ? Number(width) : undefined,
          height: height ? Number(height) : undefined,
        });
        i += 1;
        continue;
      }
    }

    const heading = /^(#{1,3})\s+(.+)$/.exec(line);
    if (heading?.[2]?.trim()) {
      blocks.push({ type: "heading", level: heading[1].length as 1 | 2 | 3, text: heading[2].trim() });
      i += 1;
      continue;
    }

    if (line.startsWith(">")) {
      const quote: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith(">")) {
        quote.push(lines[i].trim().replace(/^>\s?/, ""));
        i += 1;
      }
      blocks.push({ type: "blockquote", lines: quote.filter(Boolean) });
      continue;
    }

    if (/^[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[-*]\s+/, ""));
        i += 1;
      }
      blocks.push({ type: "ul", items });
      continue;
    }

    if (/^\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\.\s+/, ""));
        i += 1;
      }
      blocks.push({ type: "ol", items });
      continue;
    }

    const paragraph: string[] = [];
    while (i < lines.length && lines[i].trim()) {
      paragraph.push(lines[i].trim());
      i += 1;
    }
    blocks.push({ type: "paragraph", text: paragraph.join(" ") });
  }

  return blocks;
}

function inline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const pattern = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
  let cursor = 0;
  for (const match of text.matchAll(pattern)) {
    if (match.index > cursor) nodes.push(text.slice(cursor, match.index));
    const token = match[0];
    if (token.startsWith("**")) {
      nodes.push(<strong key={match.index}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith("`")) {
      nodes.push(<code key={match.index} className={proseClassNames.code}>{token.slice(1, -1)}</code>);
    } else {
      const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token);
      if (link) {
        const href = link[2];
        nodes.push(
          href.startsWith("/") ? (
            <Link key={match.index} to={href} className={proseClassNames.a}>
              {link[1]}
            </Link>
          ) : (
            <a key={match.index} href={href} rel="noreferrer" target="_blank" className={proseClassNames.a}>
              {link[1]}
            </a>
          ),
        );
      }
    }
    cursor = match.index + token.length;
  }
  if (cursor < text.length) nodes.push(text.slice(cursor));
  return nodes;
}

export function MarkdownBody({ markdown }: { markdown: string }) {
  const blocks = parseBlocks(markdown);
  const H1 = mdxComponents.h1;
  const H2 = mdxComponents.h2;
  const H3 = mdxComponents.h3;
  const P = mdxComponents.p;
  const Blockquote = mdxComponents.blockquote;
  const Ul = mdxComponents.ul;
  const Ol = mdxComponents.ol;
  const Li = mdxComponents.li;
  const Pre = mdxComponents.pre;

  return (
    <>
      {blocks.map((block, index) => {
        if (block.type === "heading") {
          if (block.level === 1) {
            return <H1 key={index}>{inline(block.text)}</H1>;
          }
          if (block.level === 2) {
            return <H2 key={index}>{inline(block.text)}</H2>;
          }
          return <H3 key={index}>{inline(block.text)}</H3>;
        }
        if (block.type === "blockquote") {
          return (
            <Blockquote key={index}>
              {block.lines.map((line, lineIndex) => (
                <P key={lineIndex}>{inline(line)}</P>
              ))}
            </Blockquote>
          );
        }
        if (block.type === "ul") {
          return <Ul key={index}>{block.items.map((item) => <Li key={item}>{inline(item)}</Li>)}</Ul>;
        }
        if (block.type === "ol") {
          return <Ol key={index}>{block.items.map((item) => <Li key={item}>{inline(item)}</Li>)}</Ol>;
        }
        if (block.type === "pre") {
          return <Pre key={index}><code>{block.code}</code></Pre>;
        }
        if (block.type === "image") {
          return (
            <figure key={index} className="my-10 overflow-hidden rounded-2xl border border-ink/10 dark:border-paper/12">
              <img
                src={block.src}
                alt={block.alt}
                width={block.width}
                height={block.height}
                loading="lazy"
                decoding="async"
                className="h-auto w-full"
              />
            </figure>
          );
        }
        return <P key={index}>{inline(block.text)}</P>;
      })}
    </>
  );
}
